import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

interface VideoData {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  duration: string;
  uploadDate: string;
  category: string;
  keywords: string[];
}

interface VideoWatchPageProps {
  videoId: string;
  onBack: () => void;
}

export function VideoWatchPage({ videoId, onBack }: VideoWatchPageProps) {
  const videosData: Record<string, VideoData> = {
    'deploy-breakthrough': {
      id: 'deploy-breakthrough',
      title: 'Bứt Phá Kinh Doanh: Deploy App Siêu Tốc! 2025',
      description: 'Khám phá cách triển khai ứng dụng một cách nhanh chóng và hiệu quả để bứt phá trong kinh doanh. Video hướng dẫn chi tiết từ A-Z về quy trình deploy chuyên nghiệp.',
      videoUrl: '/videos/invideo-ai-1080-Bust-Pha-Kinh-Doanh-Deploy-App-Sieu-Toc-2025-10-04.mp4',
      duration: '5:30',
      uploadDate: '2025-10-04',
      category: 'Tutorial',
      keywords: ['deploy', 'app deployment', 'business growth', 'tutorial', 'fast deployment']
    },
    'deploy-web-app': {
      id: 'deploy-web-app',
      title: 'Triển khai App/Web Siêu Tốc – Giá Sốc 2025',
      description: 'Dịch vụ triển khai ứng dụng web và mobile với giá cả hợp lý nhất thị trường. Quy trình tự động hóa, đảm bảo chất lượng và tốc độ vượt trội.',
      videoUrl: '/videos/invideo-ai-1080-Trien-khai-App-Web-Sieu-Toc-Gia-Soc-2025-10-04.mp4',
      duration: '4:45',
      uploadDate: '2025-10-04',
      category: 'Service Introduction',
      keywords: ['web deployment', 'mobile app', 'affordable pricing', 'fast service', 'deployment service']
    }
  };

  const video = videosData[videoId];

  useEffect(() => {
    if (video) {
      // Update page title
      document.title = `${video.title} - DeployPro`;
      
      // Add JSON-LD structured data for Google
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": video.title,
        "description": video.description,
        "thumbnailUrl": "https://deploypro.vercel.app/logo.svg",
        "uploadDate": video.uploadDate,
        "duration": `PT${video.duration.replace(':', 'M')}S`,
        "contentUrl": `https://deploypro.vercel.app${video.videoUrl}`,
        "embedUrl": `https://deploypro.vercel.app/watch?v=${video.id}`,
        "publisher": {
          "@type": "Organization",
          "name": "DeployPro",
          "logo": {
            "@type": "ImageObject",
            "url": "https://deploypro.vercel.app/logo.svg"
          }
        },
        "keywords": video.keywords.join(", "),
        "category": video.category
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [video]);

  if (!video) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Video not found</h1>
          <button onClick={onBack} className="text-emerald-400 hover:text-emerald-300">
            Return to homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="container mx-auto flex items-center">
          <button 
            onClick={onBack}
            className="flex items-center text-emerald-400 hover:text-emerald-300"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to DeployPro
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-xl mb-6">
          <video
            className="w-full h-full object-cover"
            src={video.videoUrl}
            controls
            preload="metadata"
            poster="https://deploypro.vercel.app/logo.svg"
          />
        </div>

        <div className="bg-slate-800 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-white mb-4">{video.title}</h1>
          
          <div className="flex items-center space-x-4 text-sm text-slate-400 mb-4">
            <span>{video.category}</span>
            <span>•</span>
            <span>Uploaded on {new Date(video.uploadDate).toLocaleDateString()}</span>
            <span>•</span>
            <span>{video.duration}</span>
          </div>
          
          <p className="text-slate-300 leading-relaxed mb-4">{video.description}</p>
          
          <div className="flex flex-wrap gap-2">
            {video.keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-slate-700 text-slate-300 text-sm rounded-full"
              >
                #{keyword}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}